// Adjuntos del chat del Asistente — lo que el usuario "engancha" desde el
// menú "+" antes de mandar un mensaje: una imagen de referencia, un producto
// de su catálogo o un modelo. Aparecen como chips arriba del input y, al
// enviar, se inyectan como contexto para el agente (y, en el caso de la
// imagen, como reference_image en la generación libre).

import { supabase } from '../../lib/supabase';
import { normalizeImageForUpload } from '../../utils/heicConvert';
import type { ModelSelection } from './ModelPicker';

// Mismo bucket que usa el editor de video para assets; subimos bajo una
// carpeta company-scoped sin crear fila en DB (es un adjunto efímero del
// chat, no un asset administrado).
const BUCKET = 'video-project-assets';
const SIGNED_TTL = 3600;

export type ChatAttachment =
  | {
      kind: 'image';
      id: string;
      /** URL para mostrar la miniatura del chip (firmada). */
      previewUrl: string;
      /** Path en storage — se re-firma al generar para pasarlo como ref. */
      storagePath: string;
      /** Bucket donde vive `storagePath`. Las imágenes subidas van al de
       *  assets; las "ya generadas" viven en `generadas`. Default el de
       *  assets. */
      bucket?: string;
    }
  | {
      kind: 'product';
      id: string;
      /** id real del producto (variante) en el catálogo. */
      productId: string;
      name: string;
      imageUrl: string;
    }
  | {
      kind: 'model';
      id: string;
      /** Selección de modelo (preset / auto / custom / upload). */
      selection: ModelSelection;
      name: string;
      imageUrl: string | null;
    };

/** Sube una imagen de referencia al bucket y devuelve su path + URL firmada
 *  para preview. No inserta fila en DB (adjunto efímero del chat). */
export async function uploadReferenceImage(
  file: File,
  companyId: string,
): Promise<{ storagePath: string; previewUrl: string }> {
  const normalized = await normalizeImageForUpload(file);
  const ext =
    (normalized.name.match(/\.([a-zA-Z0-9]+)$/)?.[1] || 'jpg').toLowerCase();
  const storagePath = `${companyId}/agent-refs/${crypto.randomUUID()}.${ext}`;

  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, normalized, {
      contentType: normalized.type || undefined,
      upsert: false,
    });
  if (uploadErr) throw new Error(`No se pudo subir la imagen: ${uploadErr.message}`);

  const signed = await signReferenceImage(storagePath);
  return { storagePath, previewUrl: signed || '' };
}

/** Firma un path del bucket para pasarlo como reference_image / preview.
 *  `bucket` default el de assets; las imágenes ya generadas usan `generadas`. */
export async function signReferenceImage(
  storagePath: string,
  bucket: string = BUCKET,
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, SIGNED_TTL);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}
